'use client'

import { useEffect, useState } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useSupabase } from '@/hooks/useSupabase'
import { NotificationsProvider } from '@/layout/provider/NotificationsProvider'

// Supabase 에러 코드 상수
const COLUMN_NOT_EXISTS_ERROR_CODES = ['42703', 'PGRST116', '400']

export default function FreelancerLayout({
  children,
}: Readonly<{
  children: React.ReactNode
}>) {
  const router = useRouter()
  const pathname = usePathname()
  const { supabase, user, loading: authLoading } = useSupabase()
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [isCheckingProfile, setIsCheckingProfile] = useState(false)

  const isPublicFreelancerRoute =
    (pathname?.startsWith('/freelancer/profile/') ?? false) ||
    (pathname?.startsWith('/freelancer/teams/') ?? false)

  // 비로그인 상태에서 프리랜서 페이지 접근 시 /auth로 리다이렉트
  useEffect(() => {
    if (authLoading) return

    if (isPublicFreelancerRoute) {
      setIsCheckingAuth(false)
      return
    }

    if (!supabase) {
      const currentPath = pathname || '/'
      router.push(`/auth?redirect=${encodeURIComponent(currentPath)}&role=freelancer`)
      setIsCheckingAuth(false)
      return
    }

    if (!user) {
      const checkSession = async () => {
        setIsCheckingAuth(true)
        try {
          const { data: { user }, error } = await supabase.auth.getUser()

          if (error || !user) {
            const currentPath = pathname || '/'
            router.push(`/auth?redirect=${encodeURIComponent(currentPath)}&role=freelancer`)
            return
          }
        } catch (error) {
          console.error('[freelancer layout] 사용자 확인 실패:', error)
          const currentPath = pathname || '/'
          router.push(`/auth?redirect=${encodeURIComponent(currentPath)}&role=freelancer`)
        } finally {
          setIsCheckingAuth(false)
        }
      }

      checkSession()
    } else {
      setIsCheckingAuth(false)
    }
  }, [authLoading, user, router, pathname, supabase, isPublicFreelancerRoute])

  // 프리랜서 프로필 및 온보딩 체크 (로그인된 사용자만)
  useEffect(() => {
    if (authLoading || !user || isCheckingAuth || !supabase || isPublicFreelancerRoute) return

    const checkProfile = async () => {
      setIsCheckingProfile(true)
      try {
        const { data: clientData } = await supabase
          .from('client')
          .select('user_id')
          .eq('user_id', user.id)
          .maybeSingle()

        if (!clientData) {
          const { data: profile, error: profileError } = await supabase
            .from('accounts')
            .select('profile_id, is_onboarded')
            .eq('user_id', user.id)
            .eq('is_active', true)
            .is('deleted_at', null)
            .maybeSingle()

          if (profileError) {
            if (COLUMN_NOT_EXISTS_ERROR_CODES.includes(profileError.code || '')) {
              if (!profile) {
                router.push('/onboarding')
                return
              }
              return
            }
            console.warn('프로필 상태 확인 실패:', profileError)
            return
          }

          if (!profile) {
            router.push('/onboarding')
            return
          }

          if (profile.is_onboarded === false) {
            router.push('/onboarding')
            return
          }
        }
      } catch (error) {
        console.error('프로필 체크 실패:', error)
      } finally {
        setIsCheckingProfile(false)
      }
    }

    checkProfile()
  }, [authLoading, user, router, supabase, isCheckingAuth, isPublicFreelancerRoute])

  const Shell = ({ children }: { children: React.ReactNode }) => (
      <>
      {/* ✅ Header는 최상단 app/layout.tsx에서 한 번만 렌더링됨 */}
      <NotificationsProvider enabled>
        <main className="w-full min-h-[calc(100vh-64px)] pt-[64px] md:pt-[65px]">
          {/* ✅ 컨테이너는 여기서만 통일 */}
          <div className="w-full px-4 sm:px-6 lg:px-8 md:max-w-[90%] md:mx-auto">
            {/* ✅ 상단 여백(레이아웃이 관리) */}
            <div className="w-full py-8">
              {/* ✅ children 폭 강제(오른쪽 비는 문제 방지) */}
              <div className="w-full">{children}</div>
            </div>
          </div>
        </main>
      </NotificationsProvider>
    </>
  )

  if (authLoading || (!isPublicFreelancerRoute && (isCheckingAuth || isCheckingProfile))) {
    return (
      <Shell>
        <div className="flex items-center justify-center min-h-[320px] w-full">
          <div className="text-palette-coolNeutral-60">로딩 중...</div>
        </div>
      </Shell>
    )
  }

  return <Shell>{children}</Shell>
}
